import {
  agentLogoBidReview,
  agentLogoCodeResearch,
  agentLogoContractReview,
  agentLogoDataCenter,
  agentLogoDesignPackageReview,
  agentLogoDueDiligence,
  agentLogoEnvironmentReport,
  agentLogoFloorPlan,
  agentLogoGeneralChat,
  agentLogoGeotechnicalExtraction,
  agentLogoLoadCalculation,
  agentLogoLuminaireSchedule,
  agentLogoManagementOperationsMaintenance,
  agentLogoMeetingSecretary,
  agentLogoMep,
  agentLogoParking,
  agentLogoPlantRoom,
  agentLogoQa,
  agentLogoReportGeneration,
  agentLogoReports,
  agentLogoRfpRfqReview,
  agentLogoScheduleCheck,
  agentLogoSiteDueDiligence,
  agentLogoSolutionViewer,
  agentLogoSpaceAnalysis,
  agentLogoTenantRequirements,
  agentLogoTenderDocuments,
  agentLogoVersionCompare,
  agentLogoWastewaterTreatment,
  agentLogoWaterProcessSystems,
} from '@aecom-ai-engineering/design-styles/logos/agents';
import { ServiceNameType } from '@consigli/types';

/**
 * The Lightning Designer's mark, drawn here until the design system ships one:
 * a bolt with the DS's corner sparkle, on the same 40×40 rounded tile and with
 * the same classes as the package's marks, so Logos.css sizes and fills it.
 */
const agentLogoLightningDesigner = `<svg class="ds-agent-logo ds-agent-logo--brand-green" aria-hidden="true" focusable="false" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect class="ds-agent-logo__bg" width="40" height="40" rx="8"/>
<path d="M22.6 8.4L12.4 21.6C12 22.1 12.4 22.8 13 22.8H18.6L16.7 30.8C16.5 31.6 17.5 32.1 18 31.4L28.2 18.2C28.6 17.7 28.2 17 27.6 17H22L23.9 9C24.1 8.2 23.1 7.7 22.6 8.4Z" class="ds-agent-logo__mark-fill" fill="currentColor"/>
<path d="M31.2281 5.00762C31.3106 4.99786 31.3842 5.00018 31.4669 5.00039C31.8564 5.15452 32.0579 6.64016 32.5012 7.16054C33.0276 7.77841 34.8263 7.83989 34.7317 8.57765C34.5194 8.95372 33.2392 9.1568 32.7436 9.51411C32.0362 10.024 31.9878 11.682 31.5052 11.9801C31.4293 11.9845 31.2138 11.9936 31.1782 11.9418C30.8568 11.3202 30.7077 10.6078 30.4061 9.94707C30.0135 9.08674 28.1344 9.05417 28 8.46812C28.0651 7.89068 29.4999 7.83191 30.0612 7.35891C30.7992 6.73687 30.7794 5.4835 31.2281 5.00762Z" class="ds-agent-logo__mark-fill" fill="currentColor"/>
</svg>`;

/**
 * `ServiceNameType` → the design system's inline SVG mark.
 *
 * Hand-written because nothing derives: the enum is SCREAMING_SNAKE, the DS
 * filenames follow `services.<slug>`, and Figma's variant names agree with
 * neither. The package ships thirty; `agent-logo-marquee.tsx` shows the
 * directory's catalogue through this map.
 *
 * Twenty-nine map to a service here; a miss takes AGENT_MARK_FALLBACK, which is
 * the thirtieth, `general-chat`. `mep` is `ceiling-plan`, the service titled
 * "MEP Engineering": design-styles 0.28.0 settled that from Figma's
 * "MEP Engineering agents" set.
 *
 * `bid-review` was held too, until product settled it (2026-09-16):
 * Figma's "BID Review" is the card titled "Bid Review", which is OFFER. The
 * BID_REVIEW service ("Offer (Bid Review)") is off the directory's list and
 * stays unmapped.
 *
 * The thirteen added with design-styles 0.25.0 and 0.26.0 come from Figma's
 * agent-logos frame (11509:116799, superseding 11473:115938); the filenames are
 * monoweb's slugs, so Figma's "Handover Agent" is
 * `management-operations-maintenance` and its "Component Reference checker" is
 * `schedule-check`. Contract Review, Environment Report, Reports and
 * Wastewater treatment came with 0.28.0, each from its own Figma component set.
 *
 * The Lightning Designer's is the one mark not from the package; see
 * `agentLogoLightningDesigner` above.
 */
export const AGENT_MARKS: Partial<Record<ServiceNameType, string>> = {
  [ServiceNameType.CEILING_PLAN]: agentLogoMep,
  [ServiceNameType.CODE_RESEARCH_AGENT]: agentLogoCodeResearch,
  [ServiceNameType.CONTRACT_REVIEW]: agentLogoContractReview,
  [ServiceNameType.DATA_CENTER]: agentLogoDataCenter,
  [ServiceNameType.DESIGN_PACKAGE_REVIEW]: agentLogoDesignPackageReview,
  [ServiceNameType.DUE_DILIGENCE]: agentLogoDueDiligence,
  [ServiceNameType.ENVIRONMENT_REPORT_AGENT]: agentLogoEnvironmentReport,
  [ServiceNameType.FLOOR_PLAN]: agentLogoFloorPlan,
  [ServiceNameType.GEOTECHNICAL_EXTRACTION_AGENT]: agentLogoGeotechnicalExtraction,
  [ServiceNameType.LIGHTNING_DESIGNER]: agentLogoLightningDesigner,
  [ServiceNameType.LOAD_CALCULATION]: agentLogoLoadCalculation,
  [ServiceNameType.LUMINAIRE_SCHEDULE]: agentLogoLuminaireSchedule,
  [ServiceNameType.MANAGEMENT_OPERATIONS_MAINTENANCE]: agentLogoManagementOperationsMaintenance,
  [ServiceNameType.MEETING_SECRETARY]: agentLogoMeetingSecretary,
  [ServiceNameType.OFFER]: agentLogoBidReview,
  [ServiceNameType.PARKING]: agentLogoParking,
  [ServiceNameType.PLANT_ROOM]: agentLogoPlantRoom,
  [ServiceNameType.QA_AGENT]: agentLogoQa,
  [ServiceNameType.REPORT_GENERATION]: agentLogoReportGeneration,
  [ServiceNameType.REPORTS]: agentLogoReports,
  [ServiceNameType.RFP_RFQ_REVIEW]: agentLogoRfpRfqReview,
  [ServiceNameType.SCHEDULE_CHECK]: agentLogoScheduleCheck,
  [ServiceNameType.SITE_DUE_DILIGENCE]: agentLogoSiteDueDiligence,
  [ServiceNameType.SOLUTION_VIEWER]: agentLogoSolutionViewer,
  [ServiceNameType.SPACE_ANALYSIS]: agentLogoSpaceAnalysis,
  [ServiceNameType.TENANT_REQUIREMENTS]: agentLogoTenantRequirements,
  [ServiceNameType.TENDER_DOCUMENTS]: agentLogoTenderDocuments,
  [ServiceNameType.VERSION_COMPARE]: agentLogoVersionCompare,
  [ServiceNameType.WASTEWATER_TREATMENT]: agentLogoWastewaterTreatment,
  [ServiceNameType.WATER_PROCESS_SYSTEMS_AGENT]: agentLogoWaterProcessSystems,
};

/**
 * What a service with no mark of its own shows. Agreed with design.
 *
 * The DS's generic assistant mark, which is what makes it a fallback rather than
 * an arbitrary pick — `agent-logo-marquee.tsx` records that `general-chat` is
 * deliberately not any one skill's. Of the 31 configured services it covers
 * only BID_REVIEW, which is off the directory's list.
 */
export const AGENT_MARK_FALLBACK = agentLogoGeneralChat;
